import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';

import {
  CopilotClient,
  ReminderSchedule,
  WireActionResult,
  WireActivityEntry,
  WireAddParticipantResult,
  WireAssessmentLink,
  WireCreateRequestBody,
  WireDocument,
  WireDownloadTarget,
  WireItem,
  WireItemKind,
  WireLinkedRequestDetail,
  WirePaginated,
  WireParticipant,
  WireRemoveParticipantResult,
  WireRequestAction,
  WireRequestDetail,
  WireRequestListArgs,
  WireRequestListFilters,
  WireRequestListRow,
  WireRequestStatusCounts,
  WireReviewDecision,
  WirePlacedField,
  WireSavedCsa,
  WireSection,
  WireSignatureFieldCheck,
  WireTemplate,
  WireTemplateBody,
  WireTemplateFolder,
  WireTemplateSystem,
  WireTaxLink,
  WireUploadTarget,
} from '../types/api';

/**
 * Everything the document-invite screens read and write, through Copilot's
 * proxy at `/v1/document-invite/*`.
 *
 * The proxy adds authentication and the acting-user header and forwards bodies
 * verbatim, so the Odin Document Invite service is the validation authority:
 * a 409 or 422 here came from there, and the shapes to code against are its
 * DTOs (mirrored in `types/api.ts`), not anything Copilot owns.
 *
 * Two things deliberately live outside this file:
 *
 *  - **The client portal.** `/v1/portal/*` is authenticated by the per-request
 *    token in the client's link and is not proxied — routing it through here
 *    would strip the only credential it has.
 *  - **File bytes.** `createUploadTarget` and `getDownloadTarget` return an
 *    Azure SAS URL the browser talks to directly; see `uploadToTarget` below.
 */
/**
 * The filters the request list and its status counts share, as a query string.
 *
 * One builder for both so the two can never disagree about what "the current
 * filters" are - the counts label a filter the list is applying, and a count
 * that does not match the rows under it is worse than no count.
 */
const listFilterParams = (query: WireRequestListFilters): URLSearchParams => {
  const params = new URLSearchParams();
  // Repeated keys, not a joined string: the DTO's toStringArray transform
  // accepts both.
  query.status?.forEach((value) => params.append('status', value));
  if (query.assignedTo) params.set('assignedTo', query.assignedTo);
  if (query.clientEmail) params.set('clientEmail', query.clientEmail);
  if (query.requesterEmail) params.set('requesterEmail', query.requesterEmail);
  if (query.overdue) params.set('overdue', 'true');
  if (query.atRisk) params.set('atRisk', 'true');
  if (query.search) params.set('search', query.search);
  if (query.system) params.set('system', query.system);
  return params;
};

export const documentInviteApi = createApi({
  reducerPath: 'documentInviteApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    'Request',
    'RequestList',
    // Separate from 'Request' on purpose: the roster carries portal links and
    // the detail route does not, so a screen showing links refetches when
    // somebody is added or revoked without dragging the whole detail with it.
    'Participants',
    'Template',
    'TemplateList',
    'Client',
    // The set of requests linked to a mortgage assessment, keyed by
    // assessmentId. Mutations that can change which assessment a request belongs
    // to (create-with-assessment, link, delete) invalidate it. The `LIST`
    // sentinel below covers the case where the affected assessment is not known
    // at call time: a delete carries only a requestId.
    'AssessmentDocs',
  ],
  endpoints: (builder) => ({
    /* ------------------------------------------------------------ requests */

    listRequests: builder.query<
      WirePaginated<WireRequestListRow>,
      WireRequestListArgs | void
    >({
      query: (args) => {
        const query = args ?? {};
        const params = listFilterParams(query);
        params.set('page', String(query.page ?? 1));
        // 100 is the service's ceiling. It is the default here because the
        // pickers that take no arguments - "attach an existing request" - want
        // as much of the list as one call can carry; the tracker passes its
        // own page size.
        params.set('limit', String(query.limit ?? 100));
        if (query.sort) params.set('sort', query.sort);
        if (query.dir) params.set('dir', query.dir);
        return `v1/document-invite/requests?${params.toString()}`;
      },
      transformResponse: (r: ApiResponse<WirePaginated<WireRequestListRow>>) =>
        r.data,
      providesTags: ['RequestList'],
    }),

    /**
     * Per-status totals for the same filters.
     *
     * Separate from the list on purpose: the counts only move when the filters
     * do, so pairing them with a page of rows would refetch eight numbers
     * every time somebody turned a page.
     */
    listRequestStatusCounts: builder.query<
      WireRequestStatusCounts,
      WireRequestListFilters | void
    >({
      query: (args) =>
        `v1/document-invite/requests/status-counts?${listFilterParams(
          args ?? {},
        ).toString()}`,
      transformResponse: (r: ApiResponse<WireRequestStatusCounts>) => r.data,
      providesTags: ['RequestList'],
    }),

    getRequest: builder.query<WireLinkedRequestDetail, string>({
      query: (requestId) => `v1/document-invite/requests/${requestId}`,
      transformResponse: (r: ApiResponse<WireLinkedRequestDetail>) => r.data,
      providesTags: (_result, _error, requestId) => [
        { type: 'Request', id: requestId },
      ],
    }),

    /**
     * Fetched when the Activity drawer opens and dropped when it closes, so no
     * tag: nothing else subscribes and every open refetches.
     */
    getRequestActivity: builder.query<WireActivityEntry[], string>({
      query: (requestId) => `v1/document-invite/requests/${requestId}/activity`,
      transformResponse: (r: ApiResponse<WireActivityEntry[]>) => r.data,
    }),

    /** Body documented on {@link WireCreateRequestBody}. */
    createRequest: builder.mutation<WireRequestDetail, WireCreateRequestBody>({
      query: (body) => ({
        url: 'v1/document-invite/requests',
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<WireRequestDetail>) => r.data,
      // Refetch the assessment's documents view only when this create was
      // actually linked to one.
      invalidatesTags: (_result, _error, { assessmentId }) =>
        assessmentId
          ? [
              'RequestList',
              { type: 'AssessmentDocs' as const, id: assessmentId },
            ]
          : ['RequestList'],
    }),

    /** Field edits only — status moves through {@link useRunRequestActionMutation}. */
    updateRequest: builder.mutation<
      WireRequestDetail,
      {
        requestId: string;
        body: {
          title?: string;
          dueDate?: string;
          assignedTo?: string;
          /**
           * The new automatic reminder, or **null to turn it off**. Leaving it
           * out keeps the current one: downstream applies anything not `undefined`.
           */
          reminderSchedule?: ReminderSchedule | null;
          allowClientDownload?: boolean;
        };
      }
    >({
      query: ({ requestId, body }) => ({
        url: `v1/document-invite/requests/${requestId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (r: ApiResponse<WireRequestDetail>) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    /**
     * Delete the request outright. The client's link dies with it — the portal
     * token is a hash stored on the row — and the items go too. The blobs stay
     * in Azure, orphaned.
     *
     * Almost always the wrong choice next to the `cancel` action, which keeps
     * the record and tells the client it is no longer needed. This is for a
     * request that should never have existed.
     */
    deleteRequest: builder.mutation<null, string>({
      query: (requestId) => ({
        url: `v1/document-invite/requests/${requestId}`,
        method: 'DELETE',
      }),
      transformResponse: () => null,
      // Downstream also drops any assessment mapping this request had. We only
      // hold the requestId, not the assessment it belonged to, so nudge every
      // open assessment view through the shared `LIST` sentinel.
      invalidatesTags: (_result, _error, requestId) => [
        { type: 'Request', id: requestId },
        'RequestList',
        { type: 'AssessmentDocs' as const, id: 'LIST' },
      ],
    }),

    /**
     * send | resend | remind | close | reopen | complete | cancel.
     *
     * 409s when the request is not in a status the action allows — that is the
     * downstream transition table talking, so surface its message rather than
     * guarding client-side.
     */
    runRequestAction: builder.mutation<
      WireActionResult,
      {
        requestId: string;
        action: WireRequestAction;
        message?: string;
        /**
         * Narrow a **remind** to these people; everyone when omitted.
         *
         * ⚠️ Read downstream by `remind` only. `send` and `resend` ignore it on
         * purpose — those decide who holds a live link, and issuing to a subset
         * would leave the rest of the request unreachable.
         */
        participantIds?: string[];
      }
    >({
      query: ({ requestId, action, message, participantIds }) => ({
        url: `v1/document-invite/requests/${requestId}/action`,
        method: 'POST',
        body: {
          action,
          ...(message ? { message } : {}),
          ...(participantIds ? { participantIds } : {}),
        },
      }),
      transformResponse: (r: ApiResponse<WireActionResult>) => r.data,
      /*
       * ⚠️ `Participants` too, and it is not optional. Sending is what MINTS
       * every person's portal link, and those links live only on the roster —
       * `getRequest` omits them. Leaving the roster cached means the Copy link
       * button in the People tab stays disabled after a successful send, and
       * only a manual page refresh brings it back.
       */
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        { type: 'Participants' as const, id: requestId },
        'RequestList',
      ],
    }),

    /* ---------------------------------------------------- tax file links */

    /**
     * Every request raised from a tax file, hydrated like
     * {@link useGetRequestQuery}, each with the years it covers. Links to a
     * request gone downstream are pruned server-side.
     */
    listTaxDealRequests: builder.query<WireLinkedRequestDetail[], string>({
      query: (taxDealId) =>
        `v1/document-invite/requests/by-tax-deal/${taxDealId}`,
      transformResponse: (r: ApiResponse<WireLinkedRequestDetail[]>) => r.data,
      // Each request's own tag too, so a change made on the request page
      // shows on the tax file without a reload.
      providesTags: (result) => [
        'RequestList',
        ...(result ?? []).map((request) => ({
          type: 'Request' as const,
          id: request.id,
        })),
      ],
    }),

    /** Replace the years a tax request covers; 409 for any other request. */
    setRequestTaxYears: builder.mutation<
      WireTaxLink,
      { requestId: string; financialYears: number[] }
    >({
      query: ({ requestId, financialYears }) => ({
        url: `v1/document-invite/requests/${requestId}/tax-years`,
        method: 'PUT',
        body: { financialYears },
      }),
      transformResponse: (r: ApiResponse<WireTaxLink>) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    /* -------------------------------------------------- assessment links */

    /**
     * The single request linked to a mortgage assessment, fully hydrated — same
     * shape as {@link useGetRequestQuery}, items and documents included — or
     * `null` when nothing is linked. The link is strictly 1:1 (one assessment ↔
     * one request, enforced by a unique constraint downstream), so this is never
     * an array. Stale links are pruned server-side.
     */
    getRequestByAssessment: builder.query<
      WireLinkedRequestDetail | null,
      string
    >({
      query: (assessmentId) =>
        `v1/document-invite/requests/by-assessment/${assessmentId}`,
      transformResponse: (r: ApiResponse<WireLinkedRequestDetail | null>) =>
        r.data,
      providesTags: (_result, _error, assessmentId) => [
        { type: 'AssessmentDocs', id: assessmentId },
        // Lets the mutations that cannot name the affected assessment (a request
        // delete carries only a requestId) invalidate this view anyway.
        { type: 'AssessmentDocs', id: 'LIST' },
      ],
    }),

    /**
     * Link an existing request to an assessment, or repair one whose best-effort
     * create link did not take. Idempotent for the exact same pair.
     *
     * The link is strictly 1:1, so this 409s rather than moving anything when
     * the assessment already holds a different request, or the request is
     * already linked to a different assessment; 404s for an unknown assessment.
     * Surface the downstream message instead of guarding client-side. Returns
     * the mapping row.
     */
    linkRequestToAssessment: builder.mutation<
      WireAssessmentLink,
      { requestId: string; assessmentId: string }
    >({
      query: ({ requestId, assessmentId }) => ({
        url: `v1/document-invite/requests/${requestId}/link`,
        method: 'POST',
        body: { assessmentId },
      }),
      transformResponse: (r: ApiResponse<WireAssessmentLink>) => r.data,
      invalidatesTags: (_result, _error, { assessmentId }) => [
        { type: 'AssessmentDocs' as const, id: assessmentId },
      ],
    }),

    /* ----------------------------------------------------------------- CSA */

    /**
     * The CSA behind the assessment this request is linked to. Copilot's own
     * route, not proxied - the CSA lives in Copilot.
     *
     * No tag: the CSA is written on the Recommendation page, which nothing here
     * can invalidate from. Callers refetch on mount and right before they rely
     * on it, which also renews the five-minute download link.
     */
    getSavedCsa: builder.query<WireSavedCsa, string>({
      query: (requestId) =>
        `v1/document-invite/requests/${requestId}/saved-csa`,
      transformResponse: (r: ApiResponse<WireSavedCsa>) => r.data,
    }),

    /**
     * Copilot fills the request's "CSA to e-sign" section with the picked saved
     * documents: `sign` replaces the e-sign item's PDF, `read` is shared on the
     * reading note. 409 when the section is missing, the envelope is already
     * sent, or a picked document has not been saved.
     */
    attachSavedCsa: builder.mutation<
      void,
      { requestId: string; sign: string; read: string[] }
    >({
      query: ({ requestId, sign, read }) => ({
        url: `v1/document-invite/requests/${requestId}/attach-saved-csa`,
        method: 'POST',
        body: { sign, read },
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    /* -------------------------------------------------------- participants */

    /**
     * Everyone on the request, in signer order, revoked included.
     *
     * ⚠️ Each row carries `portalUrl` — that person's own link, and a bearer
     * credential. It is here because staff genuinely need to copy one into a
     * chat when an email has not arrived. **Never log it, and never render one
     * participant another's.**
     *
     * `getRequest` carries the same roster without the links, which is what
     * most screens should read — this one is for the panel with the copy and
     * revoke buttons on it.
     */
    listParticipants: builder.query<WireParticipant[], string>({
      query: (requestId) =>
        `v1/document-invite/requests/${requestId}/participants`,
      transformResponse: (r: ApiResponse<WireParticipant[]>) => r.data,
      providesTags: (_result, _error, requestId) => [
        { type: 'Participants' as const, id: requestId },
      ],
    }),

    /**
     * Add a person to the request.
     *
     * On a draft this is a row and nothing else. On a request that has already
     * gone out it also mints their link and emails their invite — so the button
     * that calls this should say so.
     *
     * ⚠️ Check `openEnvelopes` on the result. A signing provider freezes an
     * envelope's recipients when it is created, so any item already out for
     * signature completes **without** this person; voiding and re-sending is
     * the only way to include them, and it is charged again.
     */
    addParticipant: builder.mutation<
      WireAddParticipantResult,
      {
        requestId: string;
        email: string;
        clientName?: string;
        firstName?: string;
        lastName?: string;
        message?: string;
      }
    >({
      query: ({ requestId, ...body }) => ({
        url: `v1/document-invite/requests/${requestId}/participants`,
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<WireAddParticipantResult>) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Participants' as const, id: requestId },
        { type: 'Request' as const, id: requestId },
      ],
    }),

    /**
     * Set a person's legal name, as printed on what they e-sign. Staff only.
     * It lands on the shared client row, so every request of theirs shows it.
     */
    updateParticipantFullName: builder.mutation<
      WireParticipant,
      { requestId: string; participantId: string; fullName: string }
    >({
      query: ({ requestId, participantId, fullName }) => ({
        url: `v1/document-invite/requests/${requestId}/participants/${participantId}`,
        method: 'PATCH',
        body: { fullName },
      }),
      transformResponse: (r: ApiResponse<WireParticipant>) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Participants' as const, id: requestId },
        { type: 'Request' as const, id: requestId },
        'RequestList',
      ],
    }),

    /**
     * Take a person off the request.
     *
     * Hard-deletes on a draft; **revokes** on a sent request and keeps the row,
     * so their uploads, their notes and any envelope they signed keep their
     * author. The result says which happened — read `removed` rather than
     * assuming, and it 409s if it is the primary client.
     */
    removeParticipant: builder.mutation<
      WireRemoveParticipantResult,
      { requestId: string; participantId: string }
    >({
      query: ({ requestId, participantId }) => ({
        url: `v1/document-invite/requests/${requestId}/participants/${participantId}`,
        method: 'DELETE',
      }),
      transformResponse: (r: ApiResponse<WireRemoveParticipantResult>) =>
        r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Participants' as const, id: requestId },
        { type: 'Request' as const, id: requestId },
      ],
    }),

    /**
     * **Hard revoke one person's link**, leaving them on the request.
     *
     * For a link that has escaped — forwarded on, or an invite that went to a
     * typo'd address belonging to somebody else. The next send issues them a
     * different URL.
     *
     * ⚠️ Distinct from the request-wide `revoke_link` action, which does this
     * to **everybody**, including the people whose links were fine.
     */
    revokeParticipantLink: builder.mutation<
      { id: string; email: string; linkRevoked: true },
      { requestId: string; participantId: string; reason?: string }
    >({
      query: ({ requestId, participantId, reason }) => ({
        url: `v1/document-invite/requests/${requestId}/participants/${participantId}/revoke-link`,
        method: 'POST',
        body: reason ? { reason } : {},
      }),
      transformResponse: (
        r: ApiResponse<{ id: string; email: string; linkRevoked: true }>,
      ) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Participants' as const, id: requestId },
      ],
    }),

    /** Repeatable — each apply appends the template's sections. */
    applyTemplate: builder.mutation<
      WireRequestDetail,
      { requestId: string; templateId: string }
    >({
      query: ({ requestId, templateId }) => ({
        url: `v1/document-invite/requests/${requestId}/apply-template`,
        method: 'POST',
        body: { templateId },
      }),
      transformResponse: (r: ApiResponse<WireRequestDetail>) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    /* --------------------------------------------------------------- items */

    /**
     * Items land in the band they name, appended after whatever is already
     * there, and a band that does not exist yet is created in call order - so
     * one call with the whole list reproduces the on-screen order exactly.
     *
     * ⚠️ `sectionTitle` is FIND-OR-CREATE downstream, and that is the merge:
     * two items naming `IDs` land in one `IDs`, whichever template each came
     * from. `sectionId` is the explicit form for a band that already exists;
     * sending NEITHER means ungrouped, which is a real placement rather than a
     * missing one.
     */
    addItems: builder.mutation<
      WireItem[],
      {
        requestId: string;
        items: {
          sectionId?: string;
          sectionTitle?: string;
          label: string;
          note?: string;
          kind?: WireItemKind;
          isRequired?: boolean;
          allowMultiple?: boolean;
          /** Omit for everyone on the request. */
          visibleToSignerIndexes?: number[];
        }[];
      }
    >({
      query: ({ requestId, items }) => ({
        url: `v1/document-invite/requests/${requestId}/items`,
        method: 'POST',
        body: { items },
      }),
      transformResponse: (r: ApiResponse<WireItem[]>) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    /**
     * The drag-and-drop result for ITEMS. Send every item that moved with the
     * band it landed in and its new `itemOrder`; downstream rejects the whole
     * call if any id belongs to another request, so nothing lands half-applied.
     *
     * ⚠️ There is no `sectionOrder` here any more - dragging a heading is
     * {@link useReorderSectionsMutation}, a different gesture and a different
     * call. An entry naming no `sectionId` and no `sectionTitle` moves the item
     * to the UNGROUPED run above the bands.
     */
    reorderItems: builder.mutation<
      WireItem[],
      {
        requestId: string;
        items: {
          itemId: string;
          itemOrder: number;
          sectionId?: string;
          sectionTitle?: string;
        }[];
      }
    >({
      query: ({ requestId, items }) => ({
        url: `v1/document-invite/requests/${requestId}/items/reorder`,
        method: 'PATCH',
        body: { items },
      }),
      transformResponse: (r: ApiResponse<WireItem[]>) => r.data,
      /**
       * Apply the new order to the cached detail straight away.
       *
       * Without this the dropped row springs back to where it was and only
       * settles when the refetch lands — which reads as a failed drag even
       * though it worked. On error the patch is undone and the refetch below
       * restores the truth.
       */
      /*
       * ⚠️ Re-files the cached items into the BANDS THE DRAFT ALREADY HAS,
       * rather than rebuilding the section list from the items. That is the
       * difference the new shape makes: a band the drag emptied has to stay on
       * the page (it is a row, and the accountant can drop something back into
       * it), and a band's title is not derivable from an item any more.
       *
       * ⚠️ Only an entry naming a `sectionId` can move an item on the optimistic
       * path. `sectionTitle` is find-or-create and the id it resolves to does
       * not exist here yet, so those settle on the refetch instead of guessing
       * an id the server has not minted.
       */
      async onQueryStarted({ requestId, items }, { dispatch, queryFulfilled }) {
        const order = new Map(items.map((entry) => [entry.itemId, entry]));

        const patch = dispatch(
          documentInviteApi.util.updateQueryData(
            'getRequest',
            requestId,
            (draft) => {
              const moved = draft.sections.flatMap((section) =>
                section.items.map((item) => {
                  const next = order.get(item.id);
                  if (!next) return item;
                  return {
                    ...item,
                    itemOrder: next.itemOrder,
                    sectionId: next.sectionTitle
                      ? item.sectionId
                      : (next.sectionId ?? null),
                  };
                }),
              );

              draft.sections = draft.sections.map((section) => ({
                ...section,
                items: moved
                  .filter((item) => item.sectionId === section.id)
                  .sort((a, b) => a.itemOrder - b.itemOrder),
              }));
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
      ],
    }),

    updateItem: builder.mutation<
      WireItem,
      {
        requestId: string;
        itemId: string;
        body: {
          label?: string;
          note?: string;
          isRequired?: boolean;
          allowMultiple?: boolean;
          /** Omitted leaves it alone; null opens the item to everyone. */
          visibleToSignerIndexes?: number[] | null;
        };
        /** Set by a bulk loop, which reloads the request once at the end. */
        deferRefetch?: boolean;
      }
    >({
      query: ({ requestId, itemId, body }) => ({
        url: `v1/document-invite/requests/${requestId}/items/${itemId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (r: ApiResponse<WireItem>) => r.data,
      invalidatesTags: (_result, _error, { requestId, deferRefetch }) =>
        deferRefetch ? [] : [{ type: 'Request', id: requestId }, 'RequestList'],
    }),

    /**
     * Approve, reject or waive one item.
     *
     * ⚠️ The endpoint has existed since the beginning and nothing had ever
     * called it, which made `approved`, `waived` and `rejected` states nothing
     * could produce. The row's ⋯ menu is now its caller (`reviewActions`).
     *
     * ⚠️ `reject` is accepted here and is NOT offered on any screen. It is the
     * one decision with a client-facing consequence - the reason appears on
     * their checklist and that item's dropzone reopens - and it was left out of
     * the review controls as an unintuitive third choice. The argument stays so that
     * wiring it up is a button rather than a feature.
     */
    reviewItem: builder.mutation<
      WireItem,
      {
        requestId: string;
        itemId: string;
        decision: WireReviewDecision;
        /** Required on a reject - downstream 400s without one. */
        reason?: string;
      }
    >({
      query: ({ requestId, itemId, decision, reason }) => ({
        url: `v1/document-invite/requests/${requestId}/items/${itemId}/review`,
        method: 'POST',
        body: { decision, ...(reason ? { reason } : {}) },
      }),
      transformResponse: (r: ApiResponse<WireItem>) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    removeItem: builder.mutation<
      null,
      {
        requestId: string;
        itemId: string;
        /** Set by a bulk loop, which reloads the request once at the end. */
        deferRefetch?: boolean;
      }
    >({
      query: ({ requestId, itemId }) => ({
        url: `v1/document-invite/requests/${requestId}/items/${itemId}`,
        method: 'DELETE',
      }),
      // 204 downstream, so the envelope's `data` is null and there is nothing
      // to hand back — the refetch triggered below is the result.
      transformResponse: () => null,
      invalidatesTags: (_result, _error, { requestId, deferRefetch }) =>
        deferRefetch ? [] : [{ type: 'Request', id: requestId }, 'RequestList'],
    }),

    /* ------------------------------------------------------------ sections */

    /**
     * A band on the checklist, as a thing in its own right.
     *
     * ⚠️ None of these four touch the derived status, and downstream does not
     * stamp the request's activity for them either: tidying up headings is not
     * work on the request, and floating it up the recency-ordered tracker for
     * one would make "recently active" mean "somebody renamed a heading".
     */
    addSection: builder.mutation<
      WireSection,
      { requestId: string; title: string }
    >({
      query: ({ requestId, title }) => ({
        url: `v1/document-invite/requests/${requestId}/sections`,
        method: 'POST',
        body: { title },
      }),
      transformResponse: (r: ApiResponse<WireSection>) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
      ],
    }),

    /** One write, whatever the band holds. 409 if the title is already in use. */
    renameSection: builder.mutation<
      WireSection,
      { requestId: string; sectionId: string; title: string }
    >({
      query: ({ requestId, sectionId, title }) => ({
        url: `v1/document-invite/requests/${requestId}/sections/${sectionId}`,
        method: 'PATCH',
        body: { title },
      }),
      transformResponse: (r: ApiResponse<WireSection>) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
      ],
    }),

    /**
     * ⚠️ The band goes; its items become UNGROUPED and are drawn above the
     * remaining bands. Nothing is deleted with it, which is why this needs no
     * "and everything in it" warning and must not grow one.
     */
    removeSection: builder.mutation<
      null,
      { requestId: string; sectionId: string }
    >({
      query: ({ requestId, sectionId }) => ({
        url: `v1/document-invite/requests/${requestId}/sections/${sectionId}`,
        method: 'DELETE',
      }),
      // 204 downstream, so the envelope's `data` is null
      transformResponse: () => null,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
      ],
    }),

    /**
     * Dragging a heading. Send EVERY band on the request exactly once, in
     * display order - downstream refuses a partial list, so a page that loaded
     * before someone else added a band fails loudly instead of silently pushing
     * that band to the end.
     */
    reorderSections: builder.mutation<
      WireSection[],
      { requestId: string; sectionIds: string[] }
    >({
      query: ({ requestId, sectionIds }) => ({
        url: `v1/document-invite/requests/${requestId}/sections/order`,
        method: 'PUT',
        body: { sectionIds },
      }),
      transformResponse: (r: ApiResponse<WireSection[]>) => r.data,
      /** Same reason as the item drag: a heading that springs back reads as a
       * failed drag even though it worked. */
      async onQueryStarted(
        { requestId, sectionIds },
        { dispatch, queryFulfilled },
      ) {
        const at = new Map(sectionIds.map((id, index) => [id, index]));
        const patch = dispatch(
          documentInviteApi.util.updateQueryData(
            'getRequest',
            requestId,
            (draft) => {
              draft.sections = [...draft.sections].sort(
                // the ungrouped run has no id and stays first, whatever the
                // bands do around it
                (a, b) =>
                  (a.id ? (at.get(a.id) ?? 0) : -1) -
                  (b.id ? (at.get(b.id) ?? 0) : -1),
              );
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
      ],
    }),

    /* ------------------------------- fields on a request's own e-sign item */

    /** The only place signature fields are placed, since the template PDF went. */
    getRequestItemFields: builder.query<
      { fields: WirePlacedField[]; pageCount: number | null },
      { requestId: string; itemId: string }
    >({
      query: ({ requestId, itemId }) =>
        `v1/document-invite/requests/${requestId}/items/${itemId}/fields`,
      transformResponse: (
        r: ApiResponse<{ fields: WirePlacedField[]; pageCount: number | null }>,
      ) => r.data,
      providesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
      ],
    }),

    /**
     * Whole-set replace. Two failures worth surfacing verbatim: a 400 means a
     * box would have been silently dropped by the provider, and a 409 means an
     * envelope already exists — the client may be looking at it, so it has to be
     * voided rather than edited underneath them.
     */
    putRequestItemFields: builder.mutation<
      { fields: WirePlacedField[]; pageCount: number },
      {
        requestId: string;
        itemId: string;
        fields: WirePlacedField[];
        pageCount: number;
      }
    >({
      query: ({ requestId, itemId, fields, pageCount }) => ({
        url: `v1/document-invite/requests/${requestId}/items/${itemId}/fields`,
        method: 'PUT',
        body: { fields, pageCount },
      }),
      transformResponse: (
        r: ApiResponse<{ fields: WirePlacedField[]; pageCount: number }>,
      ) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    /**
     * Void the envelope so the fields can be placed again.
     *
     * The provider holds its own frozen copy of the field set, so this is the
     * only way to correct a box once an item has been sent — editing ours would
     * diverge from what the client is actually signing.
     *
     * ⚠️ Billed: the next signing on this item creates a NEW envelope. Confirm
     * before calling, the way the send flow already does. A signed item is
     * refused downstream — that envelope is the record of the signature.
     *
     * Invalidates the request so `signatureRef` clears and the tagger unlocks.
     */
    voidItemSignature: builder.mutation<
      {
        itemId: string;
        voidedRef: string;
        status: string;
        rebilledOnNextSend: boolean;
      },
      { requestId: string; itemId: string; reason?: string }
    >({
      query: ({ requestId, itemId, reason }) => ({
        url: `v1/document-invite/requests/${requestId}/items/${itemId}/signature/void`,
        method: 'POST',
        body: reason ? { reason } : {},
      }),
      transformResponse: (
        r: ApiResponse<{
          itemId: string;
          voidedRef: string;
          status: string;
          rebilledOnNextSend: boolean;
        }>,
      ) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    /**
     * "Look again now": make the server ask the provider where this envelope
     * stands, and file the signed document if it is done.
     *
     * What support reaches for when a client says they signed and staff cannot
     * see it — the alternative was waiting up to five minutes for the server's
     * reconcile sweep. Safe to press repeatedly: the filing write is conditional,
     * so `changed: false` means it was already filed, not that anything failed.
     *
     * Invalidates the request, because the point is that `signedAt` and the
     * signed document may have just appeared on it.
     */
    refreshItemSignature: builder.mutation<
      {
        itemId: string;
        changed: boolean;
        status: string;
        signedAt: string | null;
        signatureRef: string | null;
      },
      { requestId: string; itemId: string }
    >({
      query: ({ requestId, itemId }) => ({
        url: `v1/document-invite/requests/${requestId}/items/${itemId}/signature/refresh`,
        method: 'POST',
      }),
      transformResponse: (
        r: ApiResponse<{
          itemId: string;
          changed: boolean;
          status: string;
          signedAt: string | null;
          signatureRef: string | null;
        }>,
      ) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    /**
     * What the provider holds for this item's envelope, against what would be
     * sent now.
     *
     * Not fetched on render — a `lazy` query, triggered only when someone asks
     * the question. It reaches the provider, and nobody needs it until a signed
     * document looks wrong.
     */
    getItemFieldsCheck: builder.query<
      WireSignatureFieldCheck,
      { requestId: string; itemId: string }
    >({
      query: ({ requestId, itemId }) =>
        `v1/document-invite/requests/${requestId}/items/${itemId}/signature/fields-check`,
      transformResponse: (r: ApiResponse<WireSignatureFieldCheck>) => r.data,
    }),

    /* ----------------------------------------------------------- documents */

    /** Step 1 of a staff upload — register the document, get the blob target. */
    createUploadTarget: builder.mutation<
      WireUploadTarget,
      {
        requestId: string;
        itemId: string;
        originalFilename: string;
        contentType: string;
        sizeBytes?: number;
        /**
         * Whose document this is, when staff are filing something the client
         * sent by another channel. Naming a client settles the item and decides
         * who may later open the file; omitted, it is a staff reference copy.
         */
        onBehalfOfClientId?: string;
      }
    >({
      query: ({ requestId, itemId, ...body }) => ({
        url: `v1/document-invite/requests/${requestId}/items/${itemId}/documents/upload-url`,
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<WireUploadTarget>) => r.data,
    }),

    /** Step 2 — until this lands the upload does not count towards the item. */
    confirmUpload: builder.mutation<
      WireDocument,
      {
        requestId: string;
        itemId: string;
        documentId: string;
        sizeBytes?: number;
      }
    >({
      query: ({ requestId, itemId, ...body }) => ({
        url: `v1/document-invite/requests/${requestId}/items/${itemId}/documents`,
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<WireDocument>) => r.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    removeDocument: builder.mutation<
      null,
      { requestId: string; itemId: string; documentId: string }
    >({
      query: ({ requestId, itemId, documentId }) => ({
        url: `v1/document-invite/requests/${requestId}/items/${itemId}/documents/${documentId}`,
        method: 'DELETE',
      }),
      transformResponse: () => null,
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'Request', id: requestId },
        'RequestList',
      ],
    }),

    /**
     * A mutation, not a query: a SAS read URL is short-lived, so it must be
     * minted at the moment of the click and never served from cache.
     */
    getDownloadTarget: builder.mutation<
      WireDownloadTarget,
      {
        requestId: string;
        itemId: string;
        documentId: string;
        /** `inline` for the tagger, which renders rather than saves. */
        disposition?: 'inline' | 'attachment';
      }
    >({
      query: ({ requestId, itemId, documentId, disposition }) => ({
        url: `v1/document-invite/requests/${requestId}/items/${itemId}/documents/${documentId}/download-url`,
        method: 'GET',
        params: disposition ? { disposition } : undefined,
      }),
      transformResponse: (r: ApiResponse<WireDownloadTarget>) => r.data,
    }),

    /* ----------------------------------------------------------- templates */

    /*
     * Every template call names its system: it picks the key Copilot sends,
     * and a template id alone does not say which system holds it.
     */
    listTemplates: builder.query<
      WireTemplate[],
      { system: WireTemplateSystem; folder?: string; search?: string }
    >({
      query: ({ system, folder, search }) => {
        const params = new URLSearchParams({ system });
        if (folder) params.set('folder', folder);
        if (search) params.set('search', search);
        return `v1/document-invite/templates?${params.toString()}`;
      },
      transformResponse: (r: ApiResponse<WireTemplate[]>) => r.data,
      providesTags: ['TemplateList'],
    }),

    listTemplateFolders: builder.query<
      WireTemplateFolder[],
      { system: WireTemplateSystem }
    >({
      query: ({ system }) =>
        `v1/document-invite/templates/folders?system=${system}`,
      transformResponse: (r: ApiResponse<WireTemplateFolder[]>) => r.data,
      providesTags: ['TemplateList'],
    }),

    getTemplate: builder.query<
      WireTemplate,
      { templateId: string; system: WireTemplateSystem }
    >({
      query: ({ templateId, system }) =>
        `v1/document-invite/templates/${templateId}?system=${system}`,
      transformResponse: (r: ApiResponse<WireTemplate>) => r.data,
      providesTags: (_result, _error, { templateId }) => [
        { type: 'Template', id: templateId },
      ],
    }),

    createTemplate: builder.mutation<
      WireTemplate,
      {
        system: WireTemplateSystem;
        body: { name: string; folder?: string; body: WireTemplateBody };
      }
    >({
      query: ({ system, body }) => ({
        url: `v1/document-invite/templates?system=${system}`,
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<WireTemplate>) => r.data,
      invalidatesTags: ['TemplateList'],
    }),

    updateTemplate: builder.mutation<
      WireTemplate,
      {
        templateId: string;
        system: WireTemplateSystem;
        body: {
          name?: string;
          folder?: string;
          body?: WireTemplateBody;
          isActive?: boolean;
        };
      }
    >({
      query: ({ templateId, system, body }) => ({
        url: `v1/document-invite/templates/${templateId}?system=${system}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (r: ApiResponse<WireTemplate>) => r.data,
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'Template', id: templateId },
        'TemplateList',
      ],
    }),

    duplicateTemplate: builder.mutation<
      WireTemplate,
      { templateId: string; system: WireTemplateSystem }
    >({
      query: ({ templateId, system }) => ({
        url: `v1/document-invite/templates/${templateId}/duplicate?system=${system}`,
        method: 'POST',
      }),
      transformResponse: (r: ApiResponse<WireTemplate>) => r.data,
      invalidatesTags: ['TemplateList'],
    }),

    /** Soft deactivate — templates are never hard-deleted downstream. */
    deactivateTemplate: builder.mutation<
      WireTemplate,
      { templateId: string; system: WireTemplateSystem }
    >({
      query: ({ templateId, system }) => ({
        url: `v1/document-invite/templates/${templateId}?system=${system}`,
        method: 'DELETE',
      }),
      transformResponse: (r: ApiResponse<WireTemplate>) => r.data,
      invalidatesTags: ['TemplateList'],
    }),

    /* ----------------------------- a template item's PDF and its fields ----*/

    /* ------------------------------------------------------------- clients */

    /**
     * Copilot's own client table, for the builder's picker — `clientId` on a
     * document request is one of these ids.
     *
     * The endpoint takes no filters and returns every row, so the picker
     * searches in the browser. Move to a server-side search once the table is
     * big enough for that to hurt.
     */
    /**
     * Resolve a client from a name and an email typed by hand.
     *
     * A document request needs a `clientId`, so someone who has never been
     * synced into Copilot still has to become a row before they can be asked
     * for anything. Idempotent on email: entering the same address twice
     * returns the same client, so this is safe to call on every save.
     */
    lookupClient: builder.mutation<
      CopilotClient,
      {
        email: string;
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
      }
    >({
      query: (body) => ({ url: 'clients/lookup', method: 'POST', body }),
      transformResponse: (r: ApiResponse<CopilotClient>) => r.data,
      // The picker's list is stale the moment this returns a new row.
      invalidatesTags: ['Client'],
    }),

    listClients: builder.query<CopilotClient[], void>({
      query: () => 'clients',
      transformResponse: (r: ApiResponse<CopilotClient[]>) => r.data,
      providesTags: ['Client'],
    }),
  }),
});

export const {
  useListRequestsQuery,
  useListRequestStatusCountsQuery,
  useGetRequestQuery,
  useGetRequestActivityQuery,
  useCreateRequestMutation,
  useUpdateRequestMutation,
  useRunRequestActionMutation,
  useDeleteRequestMutation,
  useGetRequestByAssessmentQuery,
  useListTaxDealRequestsQuery,
  useSetRequestTaxYearsMutation,
  useLazyGetRequestByAssessmentQuery,
  useLinkRequestToAssessmentMutation,
  useGetSavedCsaQuery,
  useAttachSavedCsaMutation,
  useApplyTemplateMutation,
  useAddItemsMutation,
  useReorderItemsMutation,
  useUpdateItemMutation,
  useRemoveItemMutation,
  useReviewItemMutation,
  useCreateUploadTargetMutation,
  useConfirmUploadMutation,
  useRemoveDocumentMutation,
  useGetDownloadTargetMutation,
  useGetRequestItemFieldsQuery,
  usePutRequestItemFieldsMutation,
  useVoidItemSignatureMutation,
  useRefreshItemSignatureMutation,
  useLazyGetItemFieldsCheckQuery,
  useListTemplatesQuery,
  useListTemplateFoldersQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDuplicateTemplateMutation,
  useDeactivateTemplateMutation,
  useListParticipantsQuery,
  useAddParticipantMutation,
  useUpdateParticipantFullNameMutation,
  useRemoveParticipantMutation,
  useRevokeParticipantLinkMutation,
  useAddSectionMutation,
  useRenameSectionMutation,
  useRemoveSectionMutation,
  useReorderSectionsMutation,
  useListClientsQuery,
  useLookupClientMutation,
} = documentInviteApi;

/**
 * PUT the bytes straight at the Azure blob target. Nothing about this call goes
 * through Copilot, which is the point — a 200 MB PDF never touches our API.
 *
 * The URL is a bearer credential with a short life (the service signs upload
 * SAS tokens for 15 minutes, scoped to this one blob, create+write only). Two
 * consequences worth knowing:
 *
 *  - **Start the PUT promptly.** A target minted and then left while the user
 *    does something else can expire before the bytes go, which Azure answers
 *    with a 403 that says nothing useful.
 *  - **Confirm promptly too.** The service reaps unconfirmed rows after 30
 *    minutes, so a blob whose `confirmUpload` never lands is collected.
 *
 * `x-ms-blob-type` is not optional: without it the container rejects the write.
 */
export const uploadToTarget = async (
  target: WireUploadTarget,
  file: File,
): Promise<void> => {
  if (!target.uploadUrl) {
    // The service signs every target it returns, so a null here means its
    // storage credentials are missing rather than anything the caller did.
    throw new Error(
      'The document service returned no upload URL — its blob storage credentials are not configured.',
    );
  }

  const response = await fetch(target.uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': target.contentType,
    },
    body: file,
  });

  if (response.ok) return;

  // Azure answers in XML nobody wants to read, so translate the three codes
  // that actually happen here into something a person can act on.
  if (response.status === 403) {
    throw new Error(
      'The upload link expired before the file finished. Try again.',
    );
  }
  if (response.status === 413) {
    throw new Error('That file is too large to upload in one piece.');
  }
  throw new Error(`Upload failed (${response.status}).`);
};
